import { useDataTable } from '@/components/data-table/data-table-provider'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '@/components/ui/input-group'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Skeleton } from '@/components/ui/skeleton'
import { formatCompactNumber } from '@/lib/format'
import { cn } from '@/lib/utils'
import { ChevronDown, Plus, Search } from 'lucide-react'
import { useState } from 'react'
import type { DataTableCheckboxFilterField, Option } from './types'
import { filterOptionsByLabel } from './utils'

const LOADING_OPTION_KEYS = [
  'loading-option-1',
  'loading-option-2',
  'loading-option-3',
]

/**
 * Compact Select-style trigger for checkbox filters. The field label sits
 * above a pill that summarizes the selection and opens a searchable
 * multi-select checkbox list.
 */
export function DataTableFilterCheckboxCompact<TData>({
  label,
  value: _value,
  options,
  component,
  triggerIcon: TriggerIcon,
  optionsState = 'ready',
  onOptionsRetry,
  creatable = false,
}: DataTableCheckboxFilterField<TData>) {
  const value = _value as string
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const {
    table,
    columnFilters,
    isLoading: isTableLoading,
    getFacetedUniqueValues,
  } = useDataTable()
  const column = table.getColumn(value)
  const filterValue = columnFilters.find((i) => i.id === value)?.value
  const facetedValue =
    getFacetedUniqueValues?.(table, value) || column?.getFacetedUniqueValues()

  const filters = filterValue
    ? Array.isArray(filterValue)
      ? filterValue
      : [filterValue]
    : []

  const summary =
    optionsState === 'loading'
      ? `Loading ${label}…`
      : optionsState === 'error'
        ? `${label} unavailable`
        : filters.length === 0
          ? 'All'
          : filters.length === 1
            ? (options?.find((o) => o.value === filters[0])?.label ??
              String(filters[0]))
            : `${filters.length} selected`

  const Component = component
  // Previously typed free-text values aren't in `options`, so they're
  // synthesized as "orphan" options here — otherwise a custom chip would
  // disappear from the list (though still applied as a filter) the moment
  // the popover reopens.
  const orphanOptions: Option[] = creatable
    ? filters
        .filter((filterValue) => !options?.some((o) => o.value === filterValue))
        .map((filterValue) => ({
          label: String(filterValue),
          value: filterValue,
        }))
    : []
  const combinedOptions =
    orphanOptions.length > 0 ? [...(options ?? []), ...orphanOptions] : options
  const filteredOptions = filterOptionsByLabel(combinedOptions, search)
  const trimmedSearch = search.trim()
  // A typed value is stored as one more array entry in the URL/filter state,
  // and BOTH the URL adapter (field.array(...).delimiter(',')) and the wire
  // converter (columnFiltersToFilterConditions' filter[field][in]=v1,v2) join
  // that array with a comma. A raw comma inside a typed value would therefore
  // silently fragment into multiple filter chips instead of matching
  // literally, so it's collapsed into a space before it can be created.
  const createableSearch = trimmedSearch.replace(/\s*,\s*/g, ' ').trim()
  const hasExactLabelMatch = combinedOptions?.some(
    (option) =>
      option.label.trim().toLocaleLowerCase() ===
      createableSearch.toLocaleLowerCase()
  )
  const showCreateOption =
    creatable && createableSearch.length > 0 && !hasExactLabelMatch

  const addCreatedValue = () => {
    if (!createableSearch) return
    column?.setFilterValue([...filters, createableSearch])
    setSearch('')
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'border-neutral-subtle-border bg-app hover:bg-app text-primary-high-contrast h-10 w-full justify-start gap-2 font-normal shadow-none'
          )}>
          {TriggerIcon ? (
            <TriggerIcon className="text-neutral-low-contrast h-4 w-4 shrink-0" />
          ) : null}
          <span className="truncate">{summary}</span>
          <ChevronDown className="text-neutral-low-contrast ml-auto h-4 w-4 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="border-neutral-subtle-border w-(--radix-popover-trigger-width) p-0">
        {optionsState === 'ready' &&
        ((options && options.length > 4) || creatable) ? (
          <div className="border-neutral-subtle-border border-b p-2">
            <InputGroup className="border-neutral-subtle-border bg-app h-9 rounded-lg shadow-none">
              <InputGroupAddon>
                <Search className="text-neutral-low-contrast h-4 w-4" />
              </InputGroupAddon>
              <InputGroupInput
                aria-label="Search options"
                placeholder={creatable ? 'Search or type to add' : 'Search'}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && showCreateOption) {
                    event.preventDefault()
                    addCreatedValue()
                  }
                }}
              />
            </InputGroup>
          </div>
        ) : null}
        <div className="max-h-[240px] overflow-y-auto">
          {optionsState === 'loading' ? (
            <div className="grid gap-2 p-3">
              {LOADING_OPTION_KEYS.map((key) => (
                <Skeleton key={key} className="h-8 w-full" />
              ))}
            </div>
          ) : null}
          {optionsState === 'error' ? (
            <div className="grid justify-items-center gap-3 px-3 py-4 text-center">
              <p className="text-neutral-low-contrast text-sm">
                Failed to load {label} options
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onOptionsRetry}>
                Retry
              </Button>
            </div>
          ) : null}
          {optionsState === 'ready'
            ? filteredOptions?.map((option, index) => {
                const checked = filters.includes(option.value)
                return (
                  <div
                    key={String(option.value)}
                    className={cn(
                      'group hover:bg-hover-ghost-bg relative flex items-center space-x-2 px-3 py-2',
                      index !== filteredOptions.length - 1
                        ? 'border-neutral-subtle-border border-b'
                        : undefined
                    )}>
                    <Checkbox
                      id={`${value}-${option.value}`}
                      checked={checked}
                      onCheckedChange={(next) => {
                        const newValue = next
                          ? [...filters, option.value]
                          : filters.filter((v) => v !== option.value)
                        column?.setFilterValue(
                          newValue.length ? newValue : undefined
                        )
                      }}
                      className="shadow-none"
                    />
                    <Label
                      htmlFor={`${value}-${option.value}`}
                      className="text-primary-high-contrast flex w-full items-center gap-1 truncate">
                      {Component ? (
                        <Component {...option} />
                      ) : (
                        <span className="truncate font-normal">
                          {option.label}
                        </span>
                      )}
                      <span className="text-neutral-low-contrast ml-auto flex items-center font-mono text-xs">
                        {isTableLoading ? (
                          <Skeleton className="h-4 w-4" />
                        ) : facetedValue?.has(option.value) ? (
                          formatCompactNumber(
                            facetedValue.get(option.value) || 0
                          )
                        ) : null}
                      </span>
                    </Label>
                  </div>
                )
              })
            : null}
          {optionsState === 'ready' && showCreateOption ? (
            <button
              type="button"
              onClick={addCreatedValue}
              className="hover:bg-hover-ghost-bg text-primary-high-contrast flex w-full items-center gap-2 px-3 py-2 text-left">
              <Plus className="text-neutral-low-contrast h-4 w-4 shrink-0" />
              <span className="truncate font-normal">
                Add “{createableSearch}”
              </span>
            </button>
          ) : null}
          {optionsState === 'ready' &&
          filteredOptions?.length === 0 &&
          !showCreateOption ? (
            <p className="text-neutral-low-contrast px-3 py-4 text-center text-sm">
              No options found
            </p>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  )
}
